import {
  mapPaginatedResponse,
  toQueryParams,
  unwrapApiData,
  type ApiClient,
} from '@klareospace/pharos-lib-front-utils';
import type {
  HistoryEntry,
  PaginatedResponse,
  PharosApiResponse,
  PharosPaginatedResponse,
  Task,
} from '@klareospace/pharos-lib-types';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Single-page fetch size for the history feed — no infinite scroll in this cycle (mirrors the orders constant). */
const TASK_HISTORY_FETCH_LIMIT = 100;

/** Single-page pagination params for the history fetch. */
const TASK_HISTORY_PAGE_PARAMS = { page: 1, pageSize: TASK_HISTORY_FETCH_LIMIT };

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Request body for PATCH /tasks/:id/assignment. Omitting/nulling `driverId` saves a carrier-only
 * assignment. `carrierId: null` unassigns the task entirely (reverts to `new`); `driverId` must be
 * null/absent in that case.
 */
export interface TaskAssignmentInput {
  carrierId: string | null;
  driverId?: string | null;
}

export type TaskHistoryResult = PaginatedResponse<HistoryEntry>;

// ─── API Factory ──────────────────────────────────────────────────────────────

export function createTasksApi(client: ApiClient): {
  assignTask: (taskId: string, data: TaskAssignmentInput) => Promise<Task>;
  getTaskHistory: (taskId: string) => Promise<TaskHistoryResult>;
  releaseTask: (taskId: string) => Promise<Task>;
  reserveTask: (taskId: string) => Promise<Task>;
} {
  return {
    assignTask: async (taskId: string, data: TaskAssignmentInput): Promise<Task> => {
      const response = await client.execute<PharosApiResponse<Task>>(() =>
        client.instance.patch(`/tasks/${taskId}/assignment`, data),
      );
      return unwrapApiData(response);
    },
    getTaskHistory: async (taskId: string): Promise<TaskHistoryResult> => {
      const query = toQueryParams(TASK_HISTORY_PAGE_PARAMS);
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<HistoryEntry>>>(() =>
        client.instance.get(`/tasks/${taskId}/history`, { params: query }),
      );
      return mapPaginatedResponse(unwrapApiData(response));
    },
    releaseTask: async (taskId: string): Promise<Task> => {
      const response = await client.execute<PharosApiResponse<Task>>(() =>
        client.instance.post(`/tasks/${taskId}/release`),
      );
      return unwrapApiData(response);
    },
    reserveTask: async (taskId: string): Promise<Task> => {
      const response = await client.execute<PharosApiResponse<Task>>(() =>
        client.instance.post(`/tasks/${taskId}/reservation`),
      );
      return unwrapApiData(response);
    },
  };
}
